import React, { useState, useEffect } from 'react';
import {
  activateCustomerDeviceNotifications,
  isOneSignalSubscribed,
  isPushCapable,
} from '../../utils/oneSignal';
import { Bell, Check, X, AlertCircle } from 'lucide-react';

export const PwaNotificationPrompt: React.FC = () => {
  const [isVisible, setIsVisible] = useState<boolean>(false);
  const [isActivating, setIsActivating] = useState<boolean>(false);
  const [isSuccess, setIsSuccess] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    // Only run on client
    if (typeof window === 'undefined' || !isPushCapable()) {
      return;
    }

    // Check if user already dismissed the prompt in this session
    try {
      const dismissed = sessionStorage.getItem('leton_pwa_notif_prompt_dismissed');
      if (dismissed === 'true') {
        return;
      }
    } catch {}

    // Check if permission is already granted or notification is already active
    isOneSignalSubscribed()
      .then((subscribed) => {
        if (!subscribed) {
          // Check browser permission state
          if (typeof Notification !== 'undefined') {
            if (Notification.permission === 'granted') {
              // Permission is already granted, activate device subscription quietly without prompt
              void activateCustomerDeviceNotifications();
              return;
            } else if (Notification.permission === 'denied') {
              // Permission denied by browser, don't show prompt
              return;
            }
          }
          // Show non-intrusive prompt after a short delay so page renders smoothly
          const timer = setTimeout(() => {
            setIsVisible(true);
          }, 1500);
          return () => clearTimeout(timer);
        }
      })
      .catch(() => {});
  }, []);

  const handleActivate = async () => {
    if (isActivating) return;
    setIsActivating(true);
    setErrorMessage(null);

    try {
      const res = await activateCustomerDeviceNotifications();
      if (res.success) {
        setIsSuccess(true);
        try {
          sessionStorage.setItem('leton_pwa_notif_prompt_dismissed', 'true');
        } catch {}
        // Auto-dismiss after 2.5s
        setTimeout(() => {
          setIsVisible(false);
        }, 2500);
      } else {
        if (res.permission === 'denied' || res.error?.includes('ditolak')) {
          setErrorMessage('Izin notifikasi diblokir di pengaturan browser.');
        } else {
          setErrorMessage(res.error || 'Izin notifikasi belum diberikan.');
        }
      }
    } catch (err: any) {
      setErrorMessage('Gagal mengaktifkan notifikasi.');
    } finally {
      setIsActivating(false);
    }
  };

  const handleDismiss = () => {
    setIsVisible(false);
    try {
      sessionStorage.setItem('leton_pwa_notif_prompt_dismissed', 'true');
    } catch {}
  };

  if (!isVisible) {
    return null;
  }

  return (
    <div className="fixed bottom-[calc(4.5rem+env(safe-area-inset-bottom,0px))] sm:bottom-20 left-4 right-4 sm:left-auto sm:right-6 sm:max-w-md z-50 animate-in fade-in slide-in-from-bottom-5 duration-300">
      <div className="bg-white/95 backdrop-blur-xl rounded-2xl p-4 sm:p-5 border border-[#E0F2FE] shadow-[0_12px_40px_rgba(2,132,199,0.12)] text-[#172033]">
        {isSuccess ? (
          <div className="flex items-center gap-3 py-1">
            <div className="w-9 h-9 rounded-xl bg-emerald-50 border border-emerald-200 text-emerald-600 flex items-center justify-center shrink-0">
              <Check className="w-5 h-5" />
            </div>
            <div className="min-w-0 flex-1">
              <h4 className="text-xs font-bold text-emerald-800">Notifikasi Aktif</h4>
              <p className="text-[11px] text-emerald-700">Perangkat Anda siap menerima update status pesanan.</p>
            </div>
          </div>
        ) : (
          <div>
            <div className="flex items-start justify-between gap-3 mb-2.5">
              <div className="flex items-center gap-2.5">
                <div className="w-9 h-9 rounded-xl bg-sky-50 border border-sky-100 text-[#0284C7] flex items-center justify-center shrink-0">
                  <Bell className="w-4 h-4" />
                </div>
                <div>
                  <h4 className="text-xs sm:text-sm font-display font-extrabold text-[#172033]">
                    Aktifkan Notifikasi Pesanan
                  </h4>
                  <span className="text-[10px] text-[#0284C7] font-bold uppercase tracking-wider">
                    LetonKu
                  </span>
                </div>
              </div>
              <button
                type="button"
                onClick={handleDismiss}
                className="p-1 rounded-lg text-[#64748B] hover:text-[#172033] hover:bg-slate-100 transition-colors cursor-pointer"
                title="Tutup"
                aria-label="Tutup"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <p className="text-xs text-[#64748B] leading-relaxed mb-3.5">
              Dapatkan pemberitahuan saat pesanan kamu diterima, sedang dibuat, atau sudah siap diambil.
            </p>

            {errorMessage && (
              <div className="mb-3 p-2.5 rounded-xl bg-rose-50 border border-rose-200 text-rose-700 text-xs flex items-center gap-2">
                <AlertCircle className="w-4 h-4 shrink-0" />
                <span>{errorMessage}</span>
              </div>
            )}

            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={handleActivate}
                disabled={isActivating}
                className="flex-1 py-2.5 px-4 rounded-xl bg-[#0284C7] hover:bg-[#0369A1] active:scale-[0.99] text-white text-xs font-bold flex items-center justify-center gap-1.5 transition-all shadow-md shadow-[#0284C7]/20 cursor-pointer disabled:opacity-50"
              >
                <Bell className="w-3.5 h-3.5" />
                <span>{isActivating ? 'Memproses Izin...' : 'AKTIFKAN NOTIFIKASI'}</span>
              </button>
              <button
                type="button"
                onClick={handleDismiss}
                className="py-2.5 px-3 rounded-xl border border-[#E0F2FE] hover:bg-slate-50 text-[#64748B] text-xs font-semibold transition-colors cursor-pointer"
              >
                Nanti saja
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
